import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Upload,
  Space,
  Tag,
  message,
  Popconfirm,
  Typography,
  Tooltip
} from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined,
  PlusOutlined,
  DownloadOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import dayjs from 'dayjs';
import { useStores } from '../../stores';
import partnerService from '../../services/partnerService';
import './InvoiceManagement.scss';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const InvoiceManagement = observer(() => {
  const { t } = useTranslation();
  const { adminPartnerStore } = useStores();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [partners, setPartners] = useState([]);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    fetchInvoices();
    fetchPartners();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    const result = await partnerService.adminFetchAllInvoices();
    if (result.success) {
      setInvoices(result.data.results || result.data);
    } else {
      message.error(result.error);
    }
    setLoading(false);
  };

  const fetchPartners = async () => {
    await adminPartnerStore.fetchAllPartners();
    setPartners(adminPartnerStore.partners);
  };

  const handleUpload = () => {
    setEditingInvoice(null);
    form.resetFields();
    setFileList([]);
    setModalVisible(true);
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    form.setFieldsValue({
      ...invoice,
      invoice_date: invoice.invoice_date ? dayjs(invoice.invoice_date) : null,
      period_start: invoice.period_start ? dayjs(invoice.period_start) : null,
      period_end: invoice.period_end ? dayjs(invoice.period_end) : null
    });
    setFileList([]);
    setModalVisible(true);
  };

  const handleDelete = async (invoiceId) => {
    const result = await partnerService.adminDeleteInvoice(invoiceId);
    if (result.success) {
      message.success(t('admin.invoices.deleteSuccess'));
      fetchInvoices();
    } else {
      message.error(result.error);
    }
  };

  const handleSubmit = async (values) => {
    const invoiceData = {
      ...values,
      invoice_date: values.invoice_date?.format('YYYY-MM-DD'),
      period_start: values.period_start?.format('YYYY-MM-DD'),
      period_end: values.period_end?.format('YYYY-MM-DD')
    };

    // Add file if selected
    if (fileList.length > 0) {
      // File can be either raw File object or object with originFileObj
      invoiceData.invoice_file = fileList[0].originFileObj || fileList[0];
    }

    let result;
    if (editingInvoice) {
      result = await partnerService.adminUpdateInvoice(editingInvoice.id, invoiceData);
    } else {
      if (!invoiceData.invoice_file) {
        message.error(t('admin.invoices.selectPdfError'));
        return;
      }
      result = await partnerService.adminUploadInvoice(invoiceData);
    }

    if (result.success) {
      message.success(editingInvoice ? t('admin.invoices.updateSuccess') : t('admin.invoices.uploadSuccess'));
      setModalVisible(false);
      fetchInvoices();
    } else {
      const errorMsg = typeof result.error === 'object'
        ? Object.values(result.error).flat().join(', ')
        : result.error;
      message.error(errorMsg);
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: 'Partner', dataIndex: 'partner_name' },
      { title: 'Title', dataIndex: 'title' },
      { title: 'Invoice Number', dataIndex: 'invoice_number' },
      {
        title: 'Amount (EUR)',
        dataIndex: 'amount',
        exportRender: (amount) => amount ? parseFloat(amount).toFixed(2) : ''
      },
      { title: 'Invoice Date', dataIndex: 'invoice_date' },
      { title: 'Period Start', dataIndex: 'period_start' },
      { title: 'Period End', dataIndex: 'period_end' },
      { title: 'Notes', dataIndex: 'notes' },
      {
        title: 'Uploaded',
        dataIndex: 'created_at',
        exportRender: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
      }
    ];
    exportToExcel(invoices, exportColumns, 'invoices', t('admin.invoices.title'));
    message.success(t('admin.invoices.exportSuccess'));
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      // Validate PDF
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        message.error(t('admin.invoices.pdfOnlyError'));
        return Upload.LIST_IGNORE;
      }
      // Validate size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.error(t('admin.invoices.fileSizeError'));
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false; // Prevent auto upload
    },
    fileList,
    accept: '.pdf'
  };

  const columns = [
    {
      title: t('admin.invoices.partner'),
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: 150,
      ellipsis: true,
      fixed: 'left',
      sorter: (a, b) => a.partner_name.localeCompare(b.partner_name),
      render: (name) => (
        <Tooltip title={name}>
          <span>{name}</span>
        </Tooltip>
      )
    },
    {
      title: t('admin.invoices.titleColumn'),
      dataIndex: 'title',
      key: 'title',
      width: 180,
      ellipsis: true,
      render: (title) => (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      )
    },
    {
      title: t('admin.invoices.invoiceNumber'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 120,
      ellipsis: true
    },
    {
      title: t('admin.invoices.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 90,
      render: (amount) => amount ? `€${parseFloat(amount).toFixed(2)}` : '-'
    },
    {
      title: t('admin.invoices.date'),
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      width: 110,
      sorter: (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date)
    },
    {
      title: t('admin.invoices.period'),
      key: 'period',
      width: 160,
      ellipsis: true,
      render: (_, record) => {
        if (record.period_start && record.period_end) {
          const period = `${record.period_start} - ${record.period_end}`;
          return (
            <Tooltip title={period}>
              <span>{period}</span>
            </Tooltip>
          );
        }
        return '-';
      }
    },
    {
      title: t('admin.invoices.file'),
      key: 'file',
      width: 130,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={record.filename || t('admin.invoices.downloadPdf')}>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            href={record.file_url}
            target="_blank"
            style={{ padding: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {record.filename ? (record.filename.length > 12 ? record.filename.substring(0, 12) + '...' : record.filename) : 'PDF'}
          </Button>
        </Tooltip>
      )
    },
    {
      title: t('admin.invoices.uploaded'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('admin.invoices.deleteConfirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="invoice-management-page">
      <Card
        title={
          <Space>
            <FileOutlined />
            <span>{t('admin.invoices.title')}</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExport}
              disabled={!invoices.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleUpload}>
              {t('admin.invoices.uploadInvoice')}
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1180 }}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={editingInvoice ? t('admin.invoices.editInvoice') : t('admin.invoices.uploadInvoice')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="partner"
            label={t('admin.invoices.partner')}
            rules={[{ required: true, message: t('admin.invoices.selectPartner') }]}
          >
            <Select
              placeholder={t('admin.invoices.selectPartnerPlaceholder')}
              showSearch
              optionFilterProp="children"
              disabled={!!editingInvoice}
            >
              {partners.map(partner => (
                <Option key={partner.id} value={partner.id}>
                  {partner.store_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label={t('admin.invoices.titleColumn')}
            rules={[{ required: true, message: t('admin.invoices.enterTitle') }]}
          >
            <Input placeholder={t('admin.invoices.titlePlaceholder')} />
          </Form.Item>

          <Form.Item name="invoice_number" label={t('admin.invoices.invoiceNumber')}>
            <Input placeholder={t('admin.invoices.invoiceNumberPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="invoice_file"
            label={t('admin.invoices.invoicePdf')}
            rules={[{ required: !editingInvoice, message: t('admin.invoices.uploadPdfRequired') }]}
          >
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                {editingInvoice ? t('admin.invoices.replacePdf') : t('admin.invoices.selectPdfFile')}
              </Button>
            </Upload>
            {editingInvoice && editingInvoice.filename && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {t('admin.invoices.currentFile')}: {editingInvoice.filename}
              </Text>
            )}
          </Form.Item>

          <Form.Item name="amount" label={t('admin.invoices.amountEur')}>
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item
            name="invoice_date"
            label={t('admin.invoices.invoiceDate')}
            rules={[{ required: true, message: t('admin.invoices.selectInvoiceDate') }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="period_start" label={t('admin.invoices.periodStart')} style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="period_end" label={t('admin.invoices.periodEnd')} style={{ flex: 1 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item name="notes" label={t('admin.invoices.notes')}>
            <TextArea rows={3} placeholder={t('admin.invoices.notesPlaceholder')} />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalVisible(false)}>{t('common.cancel')}</Button>
              <Button type="primary" htmlType="submit">
                {editingInvoice ? t('common.update') : t('common.upload')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default InvoiceManagement;
